import React, { useState } from 'react';
import { useCalificaciones } from '../hooks/useCalificaciones';
import { MatriculaResponse } from '../types/matricula.types';
import {
  NotaActividadResponse,
  NotaMasivoItem,
  esNivelLetra,
  NOTAS_LETRA,
  NOTA_LETRA_LABEL,
  NOTA_LETRA_COLOR,
} from '../types/notaActividad.types';
import { AsignacionItem } from '../types/matricula.types';
import { PeriodoResponse } from '../types/periodo.types';
import { documentoService } from 'services/documentoService';
import { exportarPDF } from 'utils/reporteExport';
import { htmlBoleta } from 'utils/documentoPdf';

// ============================================================
// CalificacionesPage – Flujo completo de calificaciones docente
//   1. Seleccionar curso
//   2. Seleccionar periodo
//   3. Ver estudiantes → registrar notas
// ============================================================

// ── Íconos ──────────────────────────────────────────────────
const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);
const D_PLUS  = 'M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z';
const D_DEL   = 'M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z';
const D_BACK  = 'M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z';

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-100 text-amber-700',
  PRIMARIA:   'bg-blue-100 text-blue-700',
  SECUNDARIA: 'bg-purple-100 text-purple-700',
};

const CARD_ACCENTS = [
  'border-t-blue-500', 'border-t-purple-500', 'border-t-green-500',
  'border-t-amber-500', 'border-t-pink-500', 'border-t-indigo-500',
];

// ── Modal para registrar actividad masiva (toda la sección) ─
type FilaForm = { nota: string; notaLetra: string | null };

const ActividadMasivaModal: React.FC<{
  estudiantes: MatriculaResponse[];
  nivelGrado:  string;
  onClose:     () => void;
  onSave:      (descripcion: string, notas: NotaMasivoItem[]) => Promise<void>;
  submitting:  boolean;
}> = ({ estudiantes, nivelGrado, onClose, onSave, submitting }) => {
  const usaLetra = esNivelLetra(nivelGrado);

  const [descripcion, setDescripcion] = useState('');
  const [filas, setFilas] = useState<Record<number, FilaForm>>(() =>
    Object.fromEntries(estudiantes.map(e => [e.idMatricula, { nota: '', notaLetra: null }]))
  );
  const [errors, setErrors]         = useState<Record<string, string>>({});
  const [localError, setLocalError] = useState<string | null>(null);

  const updateFila = (id: number, patch: Partial<FilaForm>) =>
    setFilas(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const handleSubmit = async () => {
    const e: Record<string, string> = {};
    if (!descripcion.trim()) e.descripcion = 'La descripción es requerida';

    const notasValidas: NotaMasivoItem[] = [];
    for (const est of estudiantes) {
      const fila = filas[est.idMatricula];
      if (usaLetra) {
        if (fila.notaLetra) notasValidas.push({ matriculaId: est.idMatricula, notaLetra: fila.notaLetra });
      } else {
        if (!fila.nota.trim()) continue;
        const n = parseFloat(fila.nota);
        if (isNaN(n) || n < 0 || n > 20) {
          e[`n_${est.idMatricula}`] = 'Fuera de rango';
        } else {
          notasValidas.push({ matriculaId: est.idMatricula, nota: n });
        }
      }
    }

    if (Object.keys(e).length > 0) { setErrors(e); return; }
    if (notasValidas.length === 0) { setLocalError('Ingresa al menos una nota'); return; }

    setLocalError(null);
    try {
      await onSave(descripcion.trim(), notasValidas);
      onClose();
    } catch (err: unknown) {
      setLocalError(err instanceof Error ? err.message : 'Error al guardar');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4 flex-shrink-0">
          <h3 className="text-white font-bold">Registrar Actividad</h3>
          <p className="text-blue-200 text-xs mt-0.5">{estudiantes.length} estudiantes · notas para toda la sección</p>
        </div>

        {/* Descripción */}
        <div className="px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
            Descripción de la actividad <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={descripcion}
            onChange={ev => { setDescripcion(ev.target.value); setErrors(p => { const n = {...p}; delete n.descripcion; return n; }); }}
            placeholder="Ej: Práctica calificada N°1"
            className={`w-full px-3 py-2.5 rounded-lg border text-sm text-slate-800 bg-slate-50
              placeholder-slate-300 outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
              ${errors.descripcion ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
          />
          {errors.descripcion && <span className="text-red-500 text-xs mt-1 block">{errors.descripcion}</span>}
          {!usaLetra && (
            <p className="text-slate-400 text-xs mt-2">Deja vacío el campo de nota para omitir a un estudiante.</p>
          )}
          {usaLetra && (
            <p className="text-slate-400 text-xs mt-2">Haz clic en una letra para seleccionarla; vuelve a hacer clic para deseleccionar.</p>
          )}
        </div>

        {/* Lista de estudiantes — scrollable */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          <div className="space-y-2">
            {estudiantes.map((est, idx) => {
              const fila   = filas[est.idMatricula];
              const notaErr = errors[`n_${est.idMatricula}`];
              return (
                <div key={est.idMatricula}
                  className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-2.5">
                  <span className="w-5 text-xs text-slate-400 font-semibold flex-shrink-0 text-center">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {est.estudianteApellidos}, {est.estudianteNombres}
                    </p>
                    <p className="text-xs text-slate-400">{est.estudianteDni}</p>
                  </div>

                  {usaLetra ? (
                    <div className="flex gap-1 flex-shrink-0">
                      {NOTAS_LETRA.map(letra => (
                        <button
                          key={letra}
                          type="button"
                          onClick={() => updateFila(est.idMatricula, { notaLetra: fila.notaLetra === letra ? null : letra })}
                          title={NOTA_LETRA_LABEL[letra]}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all
                            ${fila.notaLetra === letra
                              ? `${NOTA_LETRA_COLOR[letra]} border-current shadow-sm`
                              : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-100'
                            }`}
                        >
                          {letra}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex-shrink-0 w-24">
                      <input
                        type="number"
                        step="0.5"
                        min={0}
                        max={20}
                        value={fila.nota}
                        onChange={ev => {
                          updateFila(est.idMatricula, { nota: ev.target.value });
                          setErrors(p => { const n = {...p}; delete n[`n_${est.idMatricula}`]; return n; });
                        }}
                        placeholder="—"
                        className={`w-full px-2.5 py-1.5 rounded-lg border text-sm text-center text-slate-800 bg-white
                          outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                          ${notaErr ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                      />
                      {notaErr && <p className="text-red-500 text-[10px] mt-0.5 text-center">{notaErr}</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {localError && (
          <div className="px-6 pb-3 flex-shrink-0">
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
              </svg>
              <span className="text-red-600 text-sm">{localError}</span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between flex-shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors">
            Cancelar
          </button>
          <button onClick={handleSubmit} disabled={submitting}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold
                       bg-green-600 hover:bg-green-700 text-white transition-colors shadow-sm disabled:opacity-60">
            {submitting ? (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
              </svg>
            )}
            Guardar Actividad
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Selector de cursos (paso 1) ─────────────────────────────
const CursoSelector: React.FC<{
  asignaciones: AsignacionItem[];
  onSelect: (a: AsignacionItem) => void;
}> = ({ asignaciones, onSelect }) => {
  const porAnio = asignaciones.reduce<Record<number, AsignacionItem[]>>((acc, a) => {
    if (!acc[a.anio]) acc[a.anio] = [];
    acc[a.anio].push(a);
    return acc;
  }, {});
  const anios = Object.keys(porAnio).map(Number).sort((a, b) => b - a);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-800">Calificaciones</h1>
        <p className="text-slate-400 text-sm mt-0.5">Selecciona un curso para registrar notas</p>
      </div>

      {anios.map(anio => (
        <div key={anio} className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
              Año Escolar {anio}
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {porAnio[anio].map((a, idx) => (
              <button
                key={a.idAsignacion}
                onClick={() => onSelect(a)}
                className={`text-left bg-white rounded-2xl border border-slate-100 shadow-sm
                  border-t-4 ${CARD_ACCENTS[idx % CARD_ACCENTS.length]}
                  transition-all hover:shadow-md hover:-translate-y-0.5 p-5`}
              >
                <h3 className="font-bold text-slate-800 text-base mb-2">{a.cursoNombre}</h3>
                <div className="space-y-1.5">
                  <p className="text-sm text-slate-600">
                    {a.gradoNombre} — Sección <strong>{a.seccionNombre}</strong>
                  </p>
                  <span className={`inline-block text-xs font-bold px-2.5 py-1 rounded-full
                    ${NIVEL_COLOR[a.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
                    {a.gradoNivel}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Selector de periodo (paso 2) ────────────────────────────
const PeriodoSelector: React.FC<{
  periodos: PeriodoResponse[];
  loading: boolean;
  selectedId: number | null;
  onSelect: (id: number) => void;
}> = ({ periodos, loading, selectedId, onSelect }) => {
  if (loading) return <SpinnerIcon />;
  if (periodos.length === 0) {
    return <p className="text-slate-400 text-sm">No hay periodos configurados para este año escolar</p>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {periodos.map(p => (
        <button
          key={p.idPeriodo}
          onClick={() => onSelect(p.idPeriodo)}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors
            ${selectedId === p.idPeriodo
              ? 'bg-blue-700 text-white shadow-sm'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
        >
          {p.nombre}
        </button>
      ))}
    </div>
  );
};

// Colores de nota por letra para celda
const LETRA_BADGE: Record<string, string> = {
  AD: 'bg-blue-50 text-blue-700',
  A:  'bg-green-50 text-green-700',
  B:  'bg-amber-50 text-amber-700',
  C:  'bg-red-50 text-red-700',
};

// ── Modal reutilizable: corregir (PUT) o subsanar (POST) ────
const NotaCeldaModal: React.FC<{
  titulo:           string;
  accionLabel:      string;
  accionColor:      string;
  estudianteNombre: string;
  actividad:        string;
  nivelGrado:       string;
  valorInicial?:    number | string | null;
  onClose:          () => void;
  onSave:           (valor: number | string) => Promise<void>;
  submitting:       boolean;
}> = ({ titulo, accionLabel, accionColor, estudianteNombre, actividad, nivelGrado,
        valorInicial, onClose, onSave, submitting }) => {
  const usaLetra = esNivelLetra(nivelGrado);

  const [nota,     setNota]     = useState(typeof valorInicial === 'number' ? String(valorInicial) : '');
  const [notaLetra, setNotaLetra] = useState<string>(
    typeof valorInicial === 'string' ? valorInicial : 'A'
  );
  const [error,     setError]     = useState('');
  const [localErr,  setLocalErr]  = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!usaLetra) {
      const n = parseFloat(nota);
      if (!nota.trim() || isNaN(n) || n < 0 || n > 20) {
        setError('Ingresa una nota válida entre 0 y 20');
        return;
      }
      setError('');
      setLocalErr(null);
      try { await onSave(n); onClose(); }
      catch (e: unknown) { setLocalErr(e instanceof Error ? e.message : 'Error al guardar'); }
    } else {
      setLocalErr(null);
      try { await onSave(notaLetra); onClose(); }
      catch (e: unknown) { setLocalErr(e instanceof Error ? e.message : 'Error al guardar'); }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
          <h3 className="text-white font-bold">{titulo}</h3>
          <p className="text-blue-200 text-xs mt-0.5 truncate">{estudianteNombre}</p>
        </div>
        <div className="p-5 space-y-4">
          {/* Actividad (solo lectura) */}
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Actividad</p>
            <p className="text-sm text-slate-700 font-semibold bg-slate-50 rounded-lg px-3 py-2">{actividad}</p>
          </div>
          {/* Calificación */}
          {usaLetra ? (
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                Calificación <span className="text-red-500">*</span>
              </p>
              <div className="grid grid-cols-2 gap-2">
                {NOTAS_LETRA.map(letra => (
                  <button key={letra} type="button" onClick={() => setNotaLetra(letra)}
                    className={`px-4 py-3 rounded-xl text-sm font-bold border-2 transition-all
                      ${notaLetra === letra
                        ? `${NOTA_LETRA_COLOR[letra]} border-current shadow-sm`
                        : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'}`}>
                    <span className="block text-base">{letra}</span>
                    <span className="block text-xs font-normal mt-0.5 opacity-75">
                      {NOTA_LETRA_LABEL[letra].split(' — ')[1]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">
                Nota (0 – 20) <span className="text-red-500">*</span>
              </p>
              <input
                type="number" step="0.5" min={0} max={20}
                value={nota}
                onChange={e => { setNota(e.target.value); setError(''); }}
                placeholder="Ej: 16.50"
                className={`w-full px-3 py-2.5 rounded-lg border text-sm text-slate-800 bg-slate-50
                  placeholder-slate-300 outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                  ${error ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
              />
              {error && <span className="text-red-500 text-xs mt-1 block">{error}</span>}
            </div>
          )}
          {localErr && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
              <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
              </svg>
              <span className="text-red-600 text-sm">{localErr}</span>
            </div>
          )}
        </div>
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex justify-between">
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors">
            Cancelar
          </button>
          <button onClick={handleSubmit} disabled={submitting}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold
                        text-white transition-colors shadow-sm disabled:opacity-60 ${accionColor}`}>
            {submitting ? (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
              </svg>
            )}
            {accionLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal confirmación de eliminación ───────────────────────
const DeleteNotaModal: React.FC<{
  nota: NotaActividadResponse;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ nota, onConfirm, onCancel }) => {
  const valorNota = nota.notaLetra ?? nota.nota?.toFixed(2) ?? '—';
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full mx-4">
        <h3 className="font-bold text-lg text-slate-800 mb-2">¿Eliminar nota?</h3>
        <p className="text-slate-500 text-sm mb-1">
          <strong>{nota.descripcion}</strong> — <span className="font-bold">{valorNota}</span>
        </p>
        <p className="text-slate-400 text-xs mb-5">Esta acción no se puede deshacer.</p>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
            Cancelar
          </button>
          <button onClick={onConfirm}
            className="px-4 py-2 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 text-white transition-colors">
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Componente principal ────────────────────────────────────
interface CalificacionesPageProps {
  onNavigate?: (key: string) => void;
}

const CalificacionesPage: React.FC<CalificacionesPageProps> = ({ onNavigate }) => {
  const {
    asignaciones, loadingAsignaciones,
    selectedAsignacion, periodos, selectedPeriodoId, loadingPeriodos,
    estudiantes, notas, loadingNotas,
    submitting, submitError, error, exoneradosOmitidos,
    selectAsignacion, selectPeriodo,
    registrarMasivo, editarNota, subsanarNota, eliminarNota, volver,
  } = useCalificaciones();

  const [showModal, setShowModal]         = useState(false);
  const [deleteNotaId, setDeleteNotaId]   = useState<number | null>(null);
  const [editando, setEditando]           = useState<NotaActividadResponse | null>(null);
  const [subsanando, setSubsanando]       = useState<{ desc: string; est: MatriculaResponse } | null>(null);
  const [loadingBoletaId, setLoadingBoletaId] = useState<number | null>(null);
  const [boletaError, setBoletaError]     = useState<{ message: string; faltaCompetencias: boolean } | null>(null);

  const handleDescargarBoleta = async (est: MatriculaResponse) => {
    if (!selectedPeriodoId) return;
    setLoadingBoletaId(est.idMatricula);
    setBoletaError(null);
    try {
      const data = await documentoService.getBoleta(est.idMatricula, selectedPeriodoId);
      exportarPDF(`Boleta de Notas — ${est.estudianteApellidos} ${est.estudianteNombres}`, htmlBoleta(data), 'portrait');
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Error al generar la Boleta';
      // El backend devuelve 400 con un mensaje específico cuando faltan
      // competencias del periodo. Lo detectamos por substring para ofrecer
      // navegación directa al registro.
      const faltaCompetencias = /registrar las competencias/i.test(message);
      setBoletaError({ message, faltaCompetencias });
    } finally {
      setLoadingBoletaId(null);
    }
  };

  // Buscar la nota a eliminar para mostrar info en el modal
  const notaToDelete = deleteNotaId !== null ? notas.find(n => n.idNotaActividad === deleteNotaId) ?? null : null;

  // Actividades únicas en orden de aparición (columnas de la tabla)
  const actividades = React.useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    notas.forEach(n => { if (!seen.has(n.descripcion)) { seen.add(n.descripcion); list.push(n.descripcion); } });
    return list;
  }, [notas]);

  // Índice rápido: matriculaId → descripcion → nota
  const notaIdx = React.useMemo(() => {
    const idx: Record<number, Record<string, NotaActividadResponse>> = {};
    notas.forEach(n => {
      if (!idx[n.matriculaId]) idx[n.matriculaId] = {};
      idx[n.matriculaId][n.descripcion] = n;
    });
    return idx;
  }, [notas]);

  // ── Loading inicial ───────────────────────────────────────
  if (loadingAsignaciones) {
    return (
      <div className="flex justify-center items-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  // ── Error general ─────────────────────────────────────────
  if (error && !selectedAsignacion) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  // ── Sin cursos ────────────────────────────────────────────
  if (asignaciones.length === 0) {
    return (
      <div className="space-y-5">
        <h1 className="text-xl font-bold text-slate-800">Calificaciones</h1>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-16 text-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
            className="w-12 h-12 text-slate-200 mx-auto mb-3">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75Z"/>
          </svg>
          <p className="text-slate-400 text-sm font-medium">No tienes cursos asignados</p>
          <p className="text-slate-300 text-xs mt-1">Contacta al administrador para que te asigne cursos</p>
        </div>
      </div>
    );
  }

  // ── Paso 1: seleccionar curso ─────────────────────────────
  if (!selectedAsignacion) {
    return <CursoSelector asignaciones={asignaciones} onSelect={selectAsignacion} />;
  }

  // ── Paso 2+3: curso seleccionado → periodo + estudiantes ──
  return (
    <div className="space-y-5">

      {/* Header con info del curso */}
      <div className="flex items-center gap-3">
        <button onClick={volver}
          className="p-2 rounded-lg text-slate-400 hover:text-blue-700 hover:bg-slate-100 transition-colors">
          <Ico d={D_BACK} cls="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-slate-800">{selectedAsignacion.cursoNombre}</h1>
            <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full
              ${NIVEL_COLOR[selectedAsignacion.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
              {selectedAsignacion.gradoNivel}
            </span>
          </div>
          <p className="text-slate-400 text-sm">
            {selectedAsignacion.gradoNombre} — Sección {selectedAsignacion.seccionNombre} — {selectedAsignacion.anio}
          </p>
        </div>
      </div>

      {/* Selector de periodo */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Periodo</p>
        <PeriodoSelector
          periodos={periodos}
          loading={loadingPeriodos}
          selectedId={selectedPeriodoId}
          onSelect={selectPeriodo}
        />
      </div>

      {/* Tabla de estudiantes y notas */}
      {selectedPeriodoId && (
        <>
          {loadingNotas ? (
            <div className="flex justify-center py-12"><SpinnerIcon /></div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-600 text-sm">{error}</div>
          ) : estudiantes.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-12 text-center">
              <p className="text-slate-400 text-sm">No hay estudiantes matriculados en este grado y sección</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-100 overflow-hidden shadow-sm">
              {/* Botón nueva actividad */}
              <div className="px-6 py-3 border-b border-slate-100 flex justify-end">
                <button
                  onClick={() => setShowModal(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold
                             text-white bg-blue-700 hover:bg-blue-800 transition-colors shadow-sm"
                >
                  <Ico d={D_PLUS} cls="w-4 h-4" />
                  Nueva Actividad
                </button>
              </div>

              <div className="overflow-x-auto">
                {(() => {
                  const usaLetra = esNivelLetra(selectedAsignacion.gradoNivel);
                  return (
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-100">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide sticky left-0 bg-slate-50 z-10 w-8">#</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide sticky left-8 bg-slate-50 z-10 min-w-[180px]">Estudiante</th>
                          {actividades.map(desc => (
                            <th key={desc}
                              title={desc}
                              className="px-4 py-3 text-center text-xs font-bold text-slate-700 min-w-[110px] max-w-[140px]">
                              <span className="block truncate">{desc}</span>
                            </th>
                          ))}
                          {!usaLetra && (
                            <th className="px-4 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wide min-w-[90px]">Promedio</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {estudiantes.map((est, idx) => {
                          const estNotas = notaIdx[est.idMatricula] ?? {};
                          const notasNum = Object.values(estNotas)
                            .filter(n => n.nota != null).map(n => n.nota as number);
                          const promedio = !usaLetra && notasNum.length > 0
                            ? notasNum.reduce((s, v) => s + v, 0) / notasNum.length
                            : null;

                          return (
                            <tr key={est.idMatricula} className="hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-3 text-sm text-slate-400 sticky left-0 bg-inherit z-10">{idx + 1}</td>
                              <td className="px-4 py-3 sticky left-8 bg-inherit z-10">
                                <p className="text-sm font-semibold text-slate-800 whitespace-nowrap">
                                  {est.estudianteApellidos}, {est.estudianteNombres}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <p className="text-xs text-slate-400">{est.estudianteDni}</p>
                                  <button
                                    onClick={() => handleDescargarBoleta(est)}
                                    disabled={loadingBoletaId === est.idMatricula}
                                    title="Descargar Boleta de Notas"
                                    className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-1.5 py-0.5 rounded transition-colors disabled:opacity-60"
                                  >
                                    {loadingBoletaId === est.idMatricula ? '...' : 'Boleta'}
                                  </button>
                                </div>
                              </td>

                              {actividades.map(desc => {
                                const n = estNotas[desc];
                                if (!n) {
                                  return (
                                    <td key={desc} className="px-4 py-3 text-center">
                                      <button
                                        onClick={() => setSubsanando({ desc, est })}
                                        title="Registrar nota tardía"
                                        className="text-slate-300 text-xs hover:text-blue-500
                                                   hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                                      >
                                        — <span className="text-[10px]">+</span>
                                      </button>
                                    </td>
                                  );
                                }
                                const esLetra = n.notaLetra != null;
                                const badgeCls = esLetra
                                  ? (LETRA_BADGE[n.notaLetra!] ?? 'bg-slate-50 text-slate-700')
                                  : (n.nota != null && n.nota >= 11
                                      ? 'bg-green-50 text-green-700'
                                      : 'bg-red-50 text-red-700');
                                return (
                                  <td key={desc} className="px-4 py-3 text-center">
                                    <div className="group relative inline-block">
                                      <button
                                        onClick={() => setEditando(n)}
                                        title="Corregir nota"
                                        className={`inline-flex px-3 py-1 rounded-lg text-xs font-bold
                                                    transition-opacity hover:opacity-75 ${badgeCls}`}>
                                        {esLetra ? n.notaLetra : n.nota?.toFixed(2)}
                                      </button>
                                      <button
                                        onClick={() => setDeleteNotaId(n.idNotaActividad)}
                                        className="absolute -top-1.5 -right-1.5 hidden group-hover:flex
                                                   w-4 h-4 rounded-full bg-red-500 text-white
                                                   items-center justify-center text-[10px] shadow"
                                        title="Eliminar nota"
                                      >×</button>
                                    </div>
                                  </td>
                                );
                              })}

                              {!usaLetra && (
                                <td className="px-4 py-3 text-center">
                                  {promedio !== null ? (
                                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold
                                      ${promedio >= 11 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                      {promedio.toFixed(2)}
                                    </span>
                                  ) : (
                                    <span className="text-slate-300 text-xs">—</span>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            </div>
          )}

          {exoneradosOmitidos.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-amber-800 text-sm">
              <p className="font-semibold mb-1">
                ⚠️ {exoneradosOmitidos.length} alumno{exoneradosOmitidos.length !== 1 ? 's' : ''} omitido{exoneradosOmitidos.length !== 1 ? 's' : ''} por exoneración TOTAL del área:
              </p>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                {exoneradosOmitidos.map(n => <li key={n}>{n}</li>)}
              </ul>
            </div>
          )}

          {submitError && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-600 text-sm">{submitError}</div>
          )}
        </>
      )}

      {/* Modal registrar actividad masiva */}
      {showModal && (
        <ActividadMasivaModal
          estudiantes={estudiantes}
          nivelGrado={selectedAsignacion?.gradoNivel ?? ''}
          onClose={() => setShowModal(false)}
          onSave={registrarMasivo}
          submitting={submitting}
        />
      )}

      {/* Modal corregir nota existente */}
      {editando && (
        <NotaCeldaModal
          titulo="Corregir Nota"
          accionLabel="Guardar Corrección"
          accionColor="bg-amber-600 hover:bg-amber-700"
          estudianteNombre={`${editando.estudianteApellidos}, ${editando.estudianteNombres}`}
          actividad={editando.descripcion}
          nivelGrado={selectedAsignacion?.gradoNivel ?? ''}
          valorInicial={editando.notaLetra ?? editando.nota}
          onClose={() => setEditando(null)}
          onSave={valor => editarNota(editando, valor)}
          submitting={submitting}
        />
      )}

      {/* Modal subsanar nota (registro tardío) */}
      {subsanando && (
        <NotaCeldaModal
          titulo="Subsanar Nota"
          accionLabel="Registrar Nota"
          accionColor="bg-green-600 hover:bg-green-700"
          estudianteNombre={`${subsanando.est.estudianteApellidos}, ${subsanando.est.estudianteNombres}`}
          actividad={subsanando.desc}
          nivelGrado={selectedAsignacion?.gradoNivel ?? ''}
          valorInicial={null}
          onClose={() => setSubsanando(null)}
          onSave={valor => subsanarNota(subsanando.est.idMatricula, subsanando.desc, valor)}
          submitting={submitting}
        />
      )}

      {/* Modal confirmar eliminación de nota */}
      {notaToDelete && (
        <DeleteNotaModal
          nota={notaToDelete}
          onCancel={() => setDeleteNotaId(null)}
          onConfirm={() => { eliminarNota(notaToDelete.idNotaActividad); setDeleteNotaId(null); }}
        />
      )}

      {/* Modal de error al descargar boleta — incluye acción específica
          cuando el backend reporta competencias faltantes del periodo. */}
      {boletaError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
            <div className={`px-5 py-4 ${boletaError.faltaCompetencias ? 'bg-amber-50 border-b border-amber-200' : 'bg-red-50 border-b border-red-200'}`}>
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${boletaError.faltaCompetencias ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p className={`font-bold text-sm ${boletaError.faltaCompetencias ? 'text-amber-900' : 'text-red-900'}`}>
                    {boletaError.faltaCompetencias ? 'Competencias del periodo pendientes' : 'No se pudo generar la boleta'}
                  </p>
                  <p className={`text-sm mt-1 ${boletaError.faltaCompetencias ? 'text-amber-800' : 'text-red-700'}`}>
                    {boletaError.message}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 flex items-center justify-end gap-2">
              <button
                onClick={() => setBoletaError(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
              >
                Cerrar
              </button>
              {boletaError.faltaCompetencias && onNavigate && (
                <button
                  onClick={() => { setBoletaError(null); onNavigate('notas-competencias'); }}
                  className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 transition-colors shadow-sm"
                >
                  Ir a registro de competencias
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalificacionesPage;
